import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import packagingWarehouse from '../class/packagingWarehouse';
import { formatRpcError } from '../class/jrpc20WSClient';
import { fmtQty, genOperationId, perColloOf, piecesWholeFrom, sameQty, toNum } from '../class/util';
import swal from 'sweetalert';
import PkgSelect from './component/PkgSelect';
import PositionPicker from './component/PositionPicker';
import QtyBlock from './component/QtyBlock';

// Posizionamento (carico giacenza): Cliente → Divisione, codice, posizione, lotto, quantità.
// L'operatore è il magazziniere loggato (dal token, nessuna tendina).
const PosizionamentoPage = () => {
    const navigate = useNavigate();
    const ws = new packagingWarehouse();

    const [customers, setCustomers] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [components, setComponents] = useState([]);
    const [positions, setPositions] = useState([]);

    const [customerUuid, setCustomerUuid] = useState('');
    const [divisionUuid, setDivisionUuid] = useState('');
    const [componentUuid, setComponentUuid] = useState('');
    const [positionCella, setPositionCella] = useState('');
    const [lotto, setLotto] = useState('');
    // I pezzi interi NON sono uno stato: sono derivati da colli × pezzi/collo.
    const [qty, setQty] = useState({ colliWhole: '', perColloWhole: '', opens: [] });
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    // Giacenze già presenti per (divisione + codice): servono a mostrare quali PEZZATURE esistono
    // nella cella scelta. La pezzatura fa parte della chiave di riga, quindi digitandone una nuova
    // si crea una riga nuova: senza questo avviso un 300 al posto di 250 passerebbe inosservato.
    const [stockRows, setStockRows] = useState(null);

    useEffect(() => {
        Promise.all([ws.getDivisionCatalog(), ws.getComponents(), ws.getPositions()])
            .then(([cat, comp, pos]) => {
                setCustomers(cat.customers || []);
                setDivisions(cat.divisions || []);
                setComponents(comp.components || []);
                setPositions(pos.positions || []);
            })
            .catch(err => swal('Errore', formatRpcError(err), 'error'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const divisionsForCustomer = divisions.filter(d => String(d.customer_uuid) === String(customerUuid));

    const onCustomerChange = (v) => { setCustomerUuid(v); setDivisionUuid(''); };
    const updQty = (k, v) => setQty(prev => ({ ...prev, [k]: v }));
    // Colli aperti: uno per click, ognuno col suo residuo (colli diversi hanno residui diversi).
    const addOpen = () => setQty(prev => ({ ...prev, opens: [...prev.opens, ''] }));
    const updOpen = (i, v) => setQty(prev => ({ ...prev, opens: prev.opens.map((x, idx) => idx === i ? v : x) }));
    const removeOpen = (i) => setQty(prev => ({ ...prev, opens: prev.opens.filter((_, idx) => idx !== i) }));

    // Giacenze per (divisione + codice): ricaricate quando cambia una delle due.
    useEffect(() => {
        const comp = components.find(c => String(c.uuid) === String(componentUuid));
        if (!divisionUuid || !comp) { setStockRows(null); return; }
        let alive = true;
        ws.getPositionsByCode(divisionUuid, comp.code)
            .then(r => { if (alive) setStockRows(r.rows || []); })
            .catch(() => { if (alive) setStockRows([]); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [divisionUuid, componentUuid, components]);

    // Pezzature già presenti sulla chiave (cella + lotto) scelta. Con la pezzatura in chiave,
    // qui possono esserci più righe: una per formato di collo.
    const rowsForKey = (stockRows || []).filter(r => {
        const position = positions.find(p => p.cella === positionCella.trim());
        const rLotto = (r.lotto === null || r.lotto === undefined) ? '' : String(r.lotto);
        return position && r.position_uuid === position.uuid && rLotto === lotto.trim();
    });
    const existingSizes = [...new Set(rowsForKey.map(perColloOf).filter(pc => pc > 0))];
    const perColloNow = toNum(qty.perColloWhole);
    let perColloHint = null;
    if (existingSizes.length) {
        perColloHint = (perColloNow > 0 && !existingSizes.some(pc => sameQty(pc, perColloNow)))
            ? `In questa cella/lotto esistono già colli da ${existingSizes.map(fmtQty).join(', ')} pz: con ${fmtQty(perColloNow)} verrà creata una NUOVA riga.`
            : `In questa cella/lotto: colli da ${existingSizes.map(fmtQty).join(', ')} pz.`;
    }

    // Una sola pezzatura sulla chiave e campo ancora vuoto → la propone (meno battute, meno errori).
    useEffect(() => {
        if (existingSizes.length === 1 && toNum(qty.perColloWhole) <= 0) {
            // fmtQty e non String(): il campo si rilegge con toNum (formato IT), e "5.5"
            // verrebbe riletto come 55 — il punto lì è separatore di migliaia.
            setQty(prev => (toNum(prev.perColloWhole) > 0 ? prev : { ...prev, perColloWhole: fmtQty(existingSizes[0]) }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingSizes.join(','), positionCella, lotto]);

    const handleSubmit = async () => {
        const position = positions.find(p => p.cella === positionCella.trim());
        if (!divisionUuid) { swal('Attenzione', 'Selezionare la divisione', 'warning'); return; }
        if (!componentUuid) { swal('Attenzione', 'Selezionare il codice articolo', 'warning'); return; }
        if (!position) { swal('Attenzione', 'Posizione non valida: seleziona una cella esistente', 'warning'); return; }

        const cW = toNum(qty.colliWhole), perCollo = toNum(qty.perColloWhole);
        const pW = piecesWholeFrom(qty.colliWhole, qty.perColloWhole);
        const opens = qty.opens.map(v => ({ pieces: toNum(v) })).filter(o => o.pieces > 0);
        const pP = opens.reduce((sum, o) => sum + o.pieces, 0);
        // Colli interi senza pezzatura darebbero una riga con totale 0: invisibile in consultazione
        // e nel prelievo, pur avendo i colli fisicamente a scaffale.
        if (cW > 0 && perCollo <= 0) { swal('Attenzione', 'Indicare i pezzi per collo', 'warning'); return; }
        if (perCollo > 0 && cW <= 0 && pP <= 0) { swal('Attenzione', 'Indicare il numero di colli interi', 'warning'); return; }
        if (pW + pP <= 0) { swal('Attenzione', 'Inserire una quantità (pezzi)', 'warning'); return; }

        const confirm = await swal({
            title: 'Conferma Posizionamento',
            text: `Posizionare ${fmtQty(pW + pP)} pz in ${position.cella}?`,
            icon: 'info', buttons: ['Annulla', 'Conferma'],
        });
        if (!confirm) return;

        setSaving(true);
        try {
            await ws.placeMaterial({
                division_uuid: divisionUuid,
                position_uuid: position.uuid,
                component_uuid: componentUuid,
                lotto: lotto.trim(),
                qty_colli_whole: cW,
                // I pezzi interi li deriva il BE: si invia la pezzatura, che è anche il selettore
                // della riga di giacenza.
                qty_per_collo_whole: perCollo,
                // Un elemento per collo aperto: ognuno diventa una riga col suo residuo.
                opens,
                note: note.trim(),
                client_operation_id: genOperationId(),
            });
            await swal('Posizionamento effettuato', `${fmtQty(pW + pP)} pz posizionati in ${position.cella}.`, 'success');
            navigate('/home');
        } catch (err) {
            swal('Errore', formatRpcError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pkg-page">
            <div className="pkg-page-header">
                <h2 className="pkg-title">Posizionamento</h2>
            </div>

            <div className="pkg-card">
                <div className="pkg-section-title">Ubicazione</div>
                <div className="pkg-row">
                    <div className="pkg-field">
                        <label>Cliente</label>
                        <PkgSelect
                            value={customerUuid}
                            options={customers.map(c => ({ value: c.uuid, label: c.name }))}
                            onChange={onCustomerChange}
                            placeholder="Cerca cliente..."
                            noOptionsText="Nessun cliente"
                        />
                    </div>
                    <div className="pkg-field">
                        <label>Divisione</label>
                        <PkgSelect
                            value={divisionUuid}
                            options={divisionsForCustomer.map(d => ({ value: d.uuid, label: d.name }))}
                            onChange={setDivisionUuid}
                            isDisabled={!customerUuid}
                            placeholder={customerUuid ? 'Cerca divisione...' : 'Seleziona prima il cliente'}
                            noOptionsText="Nessuna divisione"
                        />
                    </div>
                </div>
                <div className="pkg-row">
                    <div className="pkg-field">
                        <label>Posizione (cella)</label>
                        <PositionPicker
                            positions={positions}
                            value={positionCella}
                            onChange={setPositionCella}
                        />
                    </div>
                </div>
                <div className="pkg-row">
                    <div className="pkg-field">
                        <label>Lotto</label>
                        <input value={lotto} onChange={e => setLotto(e.target.value)} placeholder="(nessun lotto)" />
                    </div>
                </div>
            </div>

            <div className="pkg-card">
                <div className="pkg-section-title">Articolo</div>
                <div className="pkg-row">
                    <div className="pkg-field">
                        <label>Codice articolo</label>
                        <PkgSelect
                            value={componentUuid}
                            options={components.map(c => ({ value: c.uuid, label: `${c.code} - ${c.description}` }))}
                            onChange={setComponentUuid}
                            placeholder="Cerca articolo..."
                            noOptionsText="Nessun articolo"
                        />
                    </div>
                </div>
            </div>

            <QtyBlock
                title="Quantità da aggiungere"
                colliWhole={qty.colliWhole}
                perColloWhole={qty.perColloWhole}
                opens={qty.opens}
                onAddOpen={addOpen}
                onChangeOpen={updOpen}
                onRemoveOpen={removeOpen}
                onChange={updQty}
                hint={perColloHint}
            />

            <div className="pkg-card">
                <div className="pkg-row">
                    <div className="pkg-field"><label>Note</label><textarea rows="2" value={note} onChange={e => setNote(e.target.value)} placeholder="Motivo / riferimento" /></div>
                </div>
            </div>

            <div className="pkg-actions-right">
                <button className="blue_butt" onClick={handleSubmit} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva Posizionamento'}</button>
            </div>
        </div>
    );
};

export default PosizionamentoPage;
